/**
 * One store and one sync client per shared recipe (PLAN-COLLAB C1).
 *
 * A shared recipe lives in a room, a small workspace of its own, and the
 * browser mirrors each room it is in the way it mirrors the workspace: a
 * {@link WorkspaceStore} holding the room's state, a {@link SyncClient} on the
 * room's socket, an outbox keyed `` `${userId}:room:${recipeId}` `` and an
 * IndexedDB snapshot under {@link roomSnapshotKey}. The client is the same
 * class the workspace uses, unchanged; what differs is only when it runs.
 *
 * ## A socket only while somebody is looking
 *
 * The workspace keeps one socket open for the life of the tab. A member in six
 * rooms should not keep seven, so a room's socket is **reference-counted**:
 * the editor, brew day and a batch being started call {@link RoomSources.open}
 * and the socket runs while anything holds it. Everything else — the recipe
 * list, the palette, a reload — catches up over one HTTP pull on boot and
 * again whenever the tab comes back to the front.
 *
 * A pull is only applied while the room's client is not running, and only if
 * the outbox is empty: an edit still queued for the room is not in the room's
 * state yet, and replacing the store with a pull would paint it off the
 * screen. A room with queued edits connects instead, so they flush and the
 * catch-up that follows brings the room's own answer back with them.
 *
 * ## An edit wakes the room
 *
 * A client observes its store only while started, and a command dispatched to
 * a store with nothing observing it reaches no outbox and is lost. So a write
 * to a room is bracketed by `open` and its release (the routed store does it),
 * and a room released with commands still pending keeps its socket until the
 * room has acknowledged them, then lets it go.
 *
 * ## Leaving
 *
 * A room that answers 4404, or whose pull answers 404, will never answer this
 * member again (D-CO9): its source goes `gone`, its outbox and snapshot are
 * deleted, and it is dropped. The workspace reference has already become the
 * leaver's copy by then, and arrives through the workspace's own socket.
 *
 * A reference that disappears from the workspace is stopped and dropped too,
 * but its disk is only cleared when {@link RoomSourcesOptions.canForget} says
 * the workspace can be believed: a workspace that lost its hydration race
 * opens *empty*, and treating that as "left every room" would delete edits
 * still queued for them.
 */

import type { WorkspaceState } from '@beerwright/commands';
import { workspaceStateSchema } from '@beerwright/commands';
import { withTimeout } from '@/auth/client';
import { sharedRecipePullPath, sharedRecipeSyncPath } from '@/shared-recipes/protocol';
import type { AppendLogStore, KeyValueStore } from '@/store/idb';
import { openAppendLog, openKeyValueStore } from '@/store/idb';
import type { Persistence } from '@/store/persistence';
import { createPersistence, DATABASE_NAME, roomSnapshotKey, STORE_NAME } from '@/store/persistence';
import { WorkspaceStore } from '@/store/workspace-store';
import { OUTBOX_DATABASE, OUTBOX_STORE, Outbox } from './outbox';
import type { PresenceAt, PresencePeer } from './protocol';
import { SYNC_PROTOCOL_VERSION } from './protocol';
import { sameWorkspace } from './reconcile';
import type { SyncClientOptions } from './sync-client';
import { SyncClient } from './sync-client';

/** Somebody else in a room, and where they are. */
export type Peer = PresencePeer;

/** Where a room stands on this device. */
export type RoomStatus = 'hydrating' | 'ready' | 'gone';

/** One shared recipe's room, as the client holds it. */
export interface RoomSource {
  readonly recipeId: string;
  readonly store: WorkspaceStore;
  /** `ready` once the room has been read at least once, from disk or from the server. */
  readonly status: RoomStatus;
  /** The others in the room now. Never this member. */
  readonly peers: readonly Peer[];
  /** Tell the room where this member is; `null` for nowhere in particular. */
  sendPresence(at: PresenceAt | null): void;
  /**
   * When a roster last carried a frame from `userId`, by this device's clock
   * (`Date.now()`), or `undefined` if none has. See {@link heardFrom}.
   */
  lastHeard(userId: string): number | undefined;
}

/** Whether two places on the page are the same place. */
function samePlace(a: PresenceAt, b: PresenceAt): boolean {
  return a === b || (a !== null && b !== null && a.section === b.section && a.rowId === b.rowId);
}

/**
 * Whose frame a roster answers, given the roster before it.
 *
 * The room sends the whole roster whenever anybody moves, and a roster does
 * not say who moved, so it is read off the difference: the members who
 * arrived or changed place. When nothing changed (and nobody left), the frame
 * was a member re-sending the place they were already in, and the only
 * members who do that are the ones holding the notes (D-CO11's five-second
 * re-send), so it is theirs. My own move changes my own entry, which is how
 * a roster I caused is never counted as anybody else's.
 *
 * It can over-count in one case: two members in the notes at once, when only
 * one of them re-sent. That keeps a claim alive a few seconds longer than it
 * should; it never ends one early.
 */
function heardFrom(
  before: readonly PresencePeer[],
  after: readonly PresencePeer[],
): PresencePeer[] {
  const previous = new Map(before.map((peer) => [peer.userId, peer]));
  const changed = after.filter((peer) => {
    const was = previous.get(peer.userId);
    return was === undefined || !samePlace(was.at, peer.at);
  });
  const someoneLeft = before.some((peer) => !after.some((next) => next.userId === peer.userId));
  if (changed.length > 0 || someoneLeft) return changed;
  return after.filter((peer) => peer.at?.section === 'notes');
}

/** Options for {@link RoomSources}. */
export interface RoomSourcesOptions {
  readonly userId: string;
  /** Origin the socket and the pull are addressed to. Defaults to the page's. */
  readonly origin?: string;
  /**
   * Build a room's client. Handed the options the source needs (store, outbox,
   * url, `onGone`, `onPresence`); the caller adds its own callbacks.
   */
  readonly createClient?: (options: SyncClientOptions) => SyncClient;
  readonly fetch?: typeof fetch;
  /** Snapshot storage. Defaults to IndexedDB; tests pass an in-memory one. */
  readonly snapshotStore?: KeyValueStore;
  /** Outbox storage. Defaults to IndexedDB; tests pass an in-memory one. */
  readonly outboxStore?: AppendLogStore;
  /**
   * Whether a reference missing from the workspace means the room was left.
   * Defaults to yes; the app asks whether the workspace has caught up.
   */
  readonly canForget?: () => boolean;
  readonly onError?: (error: unknown) => void;
}

/** The room socket's URL for an origin. */
export function roomSyncUrl(recipeId: string, origin: string): string {
  const url = new URL(sharedRecipeSyncPath(recipeId), origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('v', String(SYNC_PROTOCOL_VERSION));
  return url.toString();
}

/** A room's source, with the bookkeeping {@link RoomSources} keeps about it. */
class Source implements RoomSource {
  readonly recipeId: string;
  readonly store = new WorkspaceStore();
  status: RoomStatus = 'hydrating';
  peers: readonly Peer[] = [];
  /** The last roster whole, me included, for {@link heardFrom}. */
  roster: readonly Peer[] = [];
  readonly heard = new Map<string, number>();
  /** Set once the snapshot read has landed; writes start only then. */
  loaded = false;
  /** Whether the client has been started and not yet let go of. */
  running = false;
  /** How many pulls have been asked for; only the answer to the last may paint. */
  pulls = 0;
  /** The room version the screen was last painted at by a pull, or 0. */
  pulledVersion = 0;
  disposed = false;
  readonly outbox: Outbox;
  readonly persistence: Persistence;
  client!: SyncClient;
  readonly cleanups: (() => void)[] = [];

  constructor(recipeId: string, outbox: Outbox, persistence: Persistence) {
    this.recipeId = recipeId;
    this.outbox = outbox;
    this.persistence = persistence;
  }

  sendPresence(at: PresenceAt | null): void {
    if (this.disposed) return;
    this.client.sendPresence(at);
  }

  lastHeard(userId: string): number | undefined {
    return this.heard.get(userId);
  }
}

export class RoomSources {
  readonly #options: RoomSourcesOptions;
  readonly #sources = new Map<string, Source>();
  /** Rooms that said they will not answer, kept out until their reference goes. */
  readonly #gone = new Set<string>();
  /** Holds by recipe id, including holds taken before the source exists. */
  readonly #holds = new Map<string, number>();
  readonly #listeners = new Set<() => void>();
  readonly #snapshotStore: KeyValueStore;
  readonly #outboxStore: AppendLogStore;
  #all: readonly RoomSource[] = [];
  #unlistenVisibility: (() => void) | null = null;

  constructor(options: RoomSourcesOptions) {
    this.#options = options;
    this.#snapshotStore = options.snapshotStore ?? openKeyValueStore(DATABASE_NAME, STORE_NAME);
    this.#outboxStore = options.outboxStore ?? openAppendLog(OUTBOX_DATABASE, OUTBOX_STORE);
  }

  /** Diff against `workspace.sharedRecipes`: create new sources, stop and forget removed ones. */
  sync(refs: WorkspaceState['sharedRecipes']): void {
    this.#listenToVisibility();
    let changed = false;
    for (const recipeId of this.#gone) {
      if (!Object.hasOwn(refs, recipeId)) this.#gone.delete(recipeId);
    }
    for (const [recipeId, source] of this.#sources) {
      if (Object.hasOwn(refs, recipeId)) continue;
      this.#dispose(source, { forget: this.#options.canForget?.() ?? true });
      changed = true;
    }
    for (const recipeId of Object.keys(refs)) {
      if (this.#sources.has(recipeId) || this.#gone.has(recipeId)) continue;
      this.#create(recipeId);
      changed = true;
    }
    if (changed) this.#membershipChanged();
  }

  /** Reference-counted: the editor, brew day and batch start open a room's socket. */
  open(recipeId: string): () => void {
    this.#holds.set(recipeId, (this.#holds.get(recipeId) ?? 0) + 1);
    const source = this.#sources.get(recipeId);
    if (source !== undefined) this.#run(source);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const left = (this.#holds.get(recipeId) ?? 1) - 1;
      if (left > 0) this.#holds.set(recipeId, left);
      else this.#holds.delete(recipeId);
      const current = this.#sources.get(recipeId);
      if (current !== undefined) this.#idle(current);
    };
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  get(recipeId: string): RoomSource | undefined {
    return this.#sources.get(recipeId);
  }

  all(): readonly RoomSource[] {
    return this.#all;
  }

  /**
   * Stop every room and forget them, keeping what is on disk: signing out, or
   * a new identity. A later {@link sync} starts again from the snapshots.
   */
  stop(): void {
    this.#unlistenVisibility?.();
    this.#unlistenVisibility = null;
    const had = this.#sources.size > 0;
    for (const source of [...this.#sources.values()]) this.#dispose(source, { forget: false });
    this.#gone.clear();
    if (had) this.#membershipChanged();
  }

  // -------------------------------------------------------------------------
  // A source's life
  // -------------------------------------------------------------------------

  #create(recipeId: string): void {
    const { userId } = this.#options;
    const source = new Source(
      recipeId,
      new Outbox(`${userId}:room:${recipeId}`, this.#outboxStore),
      createPersistence({
        store: this.#snapshotStore,
        key: roomSnapshotKey(userId, recipeId),
        // The room's socket is what keeps two tabs agreeing; a ping between
        // them would only replace one tab's room with the other's copy of it.
        channelName: null,
        ...(this.#options.onError === undefined ? {} : { onError: this.#options.onError }),
      }),
    );
    const options: SyncClientOptions = {
      store: source.store,
      outbox: source.outbox,
      url: roomSyncUrl(recipeId, this.#origin()),
      onGone: () => this.#leave(source),
      onPresence: (peers) => {
        if (source.disposed) return;
        const now = Date.now();
        for (const peer of heardFrom(source.roster, peers)) {
          if (peer.userId !== userId) source.heard.set(peer.userId, now);
        }
        source.roster = peers;
        source.peers = peers.filter((peer) => peer.userId !== userId);
        this.#emit();
      },
    };
    source.client = this.#options.createClient?.(options) ?? new SyncClient(options);
    source.cleanups.push(
      source.store.subscribe(() => {
        if (source.loaded) source.persistence.schedule(source.store.getState());
        this.#emit();
      }),
      source.client.subscribe(() => {
        if (source.disposed) return;
        if (source.client.hasCaughtUp()) this.#settle(source);
        this.#idle(source);
      }),
    );
    this.#sources.set(recipeId, source);
    if ((this.#holds.get(recipeId) ?? 0) > 0) this.#run(source);
    void this.#boot(source);
  }

  /** Read the snapshot, then catch up. */
  async #boot(source: Source): Promise<void> {
    const untouched = source.store.getState();
    const saved = await source.persistence.load();
    if (source.disposed) return;
    source.loaded = true;
    // Only over a store nothing has reached yet: a socket that caught up
    // while the disk was being read holds a newer room than the disk does.
    if (saved !== null && source.store.getState() === untouched) {
      source.store.replaceState(saved);
      if (Object.hasOwn(saved.recipes, source.recipeId)) this.#settle(source);
    }
    await this.#refresh(source);
  }

  /** Catch a room up without its socket, or connect it if it has edits to send. */
  async #refresh(source: Source): Promise<void> {
    if (source.running || source.disposed) return;
    let queued: boolean;
    try {
      const { entries } = await source.outbox.load();
      queued = entries.length > 0;
    } catch (error) {
      this.#report(error);
      // The queue could not be read, so it may not be empty: the socket is
      // the path that is safe either way.
      queued = true;
    }
    if (source.running || source.disposed) return;
    if (queued) {
      this.#run(source);
      this.#idle(source);
      return;
    }

    const asked = ++source.pulls;
    const pulled = await this.#pull(source);
    if (pulled === null || source.running || source.disposed) return;
    // Two pulls can be in the air at once (the tab comes back to the front
    // twice), and nothing makes their answers land in the order they were
    // asked. The older one landing last used to paint the room as it was a
    // moment before over the room as it is. So only the newest request may
    // paint, and never with a room older than one already on screen, whether
    // a pull put it there or the socket did.
    if (asked !== source.pulls) return;
    const floor = Math.max(source.pulledVersion, source.client.getVersion());
    if (pulled.version < floor) return;
    source.pulledVersion = pulled.version;
    if (!sameWorkspace(pulled.state, source.store.getState())) {
      source.store.replaceState(pulled.state);
    }
    this.#settle(source);
  }

  /**
   * `GET …/pull`, or `null` when it did not answer usefully. A 404 is the room
   * leaving.
   */
  async #pull(
    source: Source,
  ): Promise<{ readonly state: WorkspaceState; readonly version: number } | null> {
    const request = this.#options.fetch ?? globalThis.fetch.bind(globalThis);
    try {
      const response = await request(
        new URL(sharedRecipePullPath(source.recipeId), this.#origin()).toString(),
        { credentials: 'same-origin', headers: { accept: 'application/json' }, ...withTimeout() },
      );
      if (response.status === 404) {
        if (!source.disposed) this.#leave(source);
        return null;
      }
      if (!response.ok) return null;
      const body = (await response.json()) as {
        readonly state?: unknown;
        readonly version?: unknown;
      };
      const parsed = workspaceStateSchema.safeParse(body.state);
      if (!parsed.success) {
        this.#report(new Error('A shared recipe arrived in a shape this build cannot read.'));
        return null;
      }
      // A version this build cannot read orders nothing, so it is taken as the
      // oldest there is rather than trusted to be the newest.
      const version =
        typeof body.version === 'number' && Number.isSafeInteger(body.version) ? body.version : 0;
      return { state: parsed.data, version };
    } catch (error) {
      this.#report(error);
      return null;
    }
  }

  /** Start the room's client, if it is not already running. */
  #run(source: Source): void {
    if (source.running || source.disposed) return;
    source.running = true;
    void source.client.start();
  }

  /**
   * Let the socket go once nothing holds it and nothing is waiting on it.
   *
   * `synced` is the client's "caught up, and every edit acknowledged".
   */
  #idle(source: Source): void {
    if (!source.running || source.disposed) return;
    if ((this.#holds.get(source.recipeId) ?? 0) > 0) return;
    if (source.client.getPendingCount() > 0 || source.client.getStatus() !== 'synced') return;
    source.running = false;
    source.client.stop();
  }

  #settle(source: Source): void {
    if (source.status !== 'hydrating') return;
    source.status = 'ready';
    this.#emit();
  }

  /** The room will not answer this member again (D-CO9). */
  #leave(source: Source): void {
    if (source.disposed) return;
    source.status = 'gone';
    this.#gone.add(source.recipeId);
    this.#dispose(source, { forget: true });
    this.#membershipChanged();
  }

  #dispose(source: Source, { forget }: { readonly forget: boolean }): void {
    if (source.disposed) return;
    source.disposed = true;
    source.running = false;
    for (const cleanup of source.cleanups) cleanup();
    source.client.stop();
    this.#sources.delete(source.recipeId);
    if (!forget) {
      void source.persistence.flush().finally(() => source.persistence.dispose());
      return;
    }
    source.persistence.dispose();
    void this.#erase(source);
  }

  /** Delete a room's snapshot and queue. */
  async #erase(source: Source): Promise<void> {
    const { userId } = this.#options;
    const results = await Promise.allSettled([
      this.#snapshotStore.delete(roomSnapshotKey(userId, source.recipeId)),
      source.outbox
        .load()
        .then(({ entries, unreadable }) =>
          source.outbox.remove([...entries.map((entry) => entry.key), ...unreadable]),
        ),
    ]);
    for (const result of results) if (result.status === 'rejected') this.#report(result.reason);
  }

  // -------------------------------------------------------------------------
  // Plumbing
  // -------------------------------------------------------------------------

  /** A tab coming back to the front catches up the rooms nobody has open. */
  #listenToVisibility(): void {
    if (this.#unlistenVisibility !== null || typeof document === 'undefined') return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      for (const source of this.#sources.values()) void this.#refresh(source);
    };
    document.addEventListener('visibilitychange', onVisible);
    this.#unlistenVisibility = () => document.removeEventListener('visibilitychange', onVisible);
  }

  #origin(): string {
    return this.#options.origin ?? globalThis.location?.origin ?? 'http://localhost';
  }

  #membershipChanged(): void {
    this.#all = [...this.#sources.values()];
    this.#emit();
  }

  #emit(): void {
    for (const listener of this.#listeners) listener();
  }

  #report(error: unknown): void {
    if (this.#options.onError !== undefined) this.#options.onError(error);
    else console.warn('[beerwright] shared recipe:', error);
  }
}
